import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

type BrandLogoProps = {
  className?: string;
  size?: "sm" | "md" | "lg";
  showHouse?: boolean;
  /** Empty by default — packaging lockup text is omitted sitewide */
  houseLabel?: string;
  to?: string | null;
};

const sizeMap = {
  sm: {
    nani: "text-[1.35rem] md:text-xl",
    recipe: "text-[0.55rem] tracking-[0.35em]",
    house: "h-7 w-7",
    label: "text-[0.5rem] tracking-[0.18em]",
    gap: "gap-1",
    leaf: "h-2.5 w-3",
  },
  md: {
    nani: "text-2xl md:text-3xl",
    recipe: "text-[0.65rem] tracking-[0.38em]",
    house: "h-9 w-9",
    label: "text-[0.58rem] tracking-[0.2em]",
    gap: "gap-1.5",
    leaf: "h-3 w-3.5",
  },
  lg: {
    nani: "text-4xl md:text-5xl",
    recipe: "text-xs tracking-[0.42em] md:text-sm",
    house: "h-12 w-12",
    label: "text-[0.7rem] tracking-[0.22em]",
    gap: "gap-2",
    leaf: "h-3.5 w-4",
  },
};

/** Matches pouch lockup: serif “Nani's”, spaced “RECIPE”, line-art house */
export function BrandLogo({
  className,
  size = "sm",
  showHouse = true,
  houseLabel = "",
  to = "/",
}: BrandLogoProps) {
  const s = sizeMap[size];
  const content = (
    <span className={cn("inline-flex flex-col items-center", s.gap, className)}>
      <span className="relative inline-block font-display font-semibold leading-none text-foreground">
        <span className={s.nani}>
          Nan
          <span className="relative inline-block leading-none">
            i
            <LeafMark
              className={cn(
                "pointer-events-none absolute left-1/2 top-[0.12em] -translate-x-1/2 -translate-y-full text-primary",
                s.leaf,
              )}
            />
          </span>
          &apos;s
        </span>
      </span>
      <span
        className={cn(
          "font-sans font-medium uppercase text-foreground/85",
          s.recipe,
        )}
      >
        Recipe
      </span>
      {showHouse ? (
        <span className="mt-0.5 flex flex-col items-center gap-0.5">
          <HouseMark className={cn("text-foreground", s.house)} />
          {houseLabel ? (
            <span
              className={cn(
                "font-sans font-medium uppercase text-accent",
                s.label,
              )}
            >
              {houseLabel}
            </span>
          ) : null}
        </span>
      ) : null}
    </span>
  );

  if (to) {
    return (
      <Link to={to} className="cursor-pointer" aria-label="Nani's Recipe home">
        {content}
      </Link>
    );
  }
  return content;
}

/** Small dual-leaf sprout for the i — not letter-like */
function LeafMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 20 16" fill="currentColor" aria-hidden className={className}>
      <path d="M10 15.5V7.5" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" />
      <ellipse cx="6.2" cy="5.2" rx="4.2" ry="2.6" transform="rotate(-35 6.2 5.2)" />
      <ellipse cx="13.8" cy="5.2" rx="4.2" ry="2.6" transform="rotate(35 13.8 5.2)" />
    </svg>
  );
}

function HouseMark({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 64 56"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={className}
    >
      <path d="M8 28 L32 8 L56 28" />
      <path d="M14 26 V48 H50 V26" />
      <path d="M26 48 V34 H38 V48" />
      <path d="M40 14 V8 H48 V20" />
      <rect x="22" y="30" width="6" height="6" />
      <rect x="36" y="30" width="6" height="6" />
    </svg>
  );
}
